import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Upload, message, Popconfirm, Alert, Tag, Space, Modal } from 'antd';
import { UploadOutlined, DownloadOutlined, DeleteOutlined, FilePdfOutlined, FileImageOutlined, FileWordOutlined, FileExcelOutlined, EyeOutlined } from '@ant-design/icons';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import getFileUrl from '../utils/fileUrl';

// ประเภทไฟล์ที่อนุญาต (ตรงกับ backend)
const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'doc', 'docx', 'xls', 'xlsx'];
const ACCEPT_ATTR = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.bmp,.doc,.docx,.xls,.xlsx';

const MIME_LABEL = {
  pdf: 'PDF',
  'jpg': 'รูปภาพ', 'jpeg': 'รูปภาพ', 'png': 'รูปภาพ', 'gif': 'รูปภาพ', 'webp': 'รูปภาพ', 'bmp': 'รูปภาพ',
  doc: 'Word', docx: 'Word',
  xls: 'Excel', xlsx: 'Excel',
};

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];

function isPdf(name) {
  return (name || '').split('.').pop().toLowerCase() === 'pdf';
}

function isImage(name) {
  return IMAGE_EXTENSIONS.includes((name || '').split('.').pop().toLowerCase());
}

// ไฟล์ที่ดูพรีวิวได้ (PDF / รูปภาพ)
function isPreviewable(name) {
  return isPdf(name) || isImage(name);
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function fileTypeLabel(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  return MIME_LABEL[ext] || ext || 'ไฟล์';
}

function fileIcon(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return <FilePdfOutlined style={{ color: '#f5222d' }} />;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return <FileImageOutlined style={{ color: '#1890ff' }} />;
  if (['doc', 'docx'].includes(ext)) return <FileWordOutlined style={{ color: '#2f54eb' }} />;
  if (['xls', 'xlsx'].includes(ext)) return <FileExcelOutlined style={{ color: '#52c41a' }} />;
  return <FilePdfOutlined />;
}

const TempleDocuments = ({ templeId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  const authToken = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchDocuments = async () => {
    if (!templeId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/temple-documents/${templeId}`, authToken());
      setDocuments(res.data.documents || []);
    } catch (err) {
      message.error('ไม่สามารถโหลดรายการเอกสารได้');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templeId]);

  const handleUpload = async (file) => {
    const ext = (file.name || '').split('.').pop().toLowerCase();

    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      message.error(`ไม่อนุญาตไฟล์ "${file.name}" — อนุญาตเฉพาะ PDF, รูปภาพ, DOCX, XLSX (ไม่อนุญาตวิดีโอ)`);
      return false; // ยกเลิก
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('templeId', templeId);

    try {
      await axios.post(`${API_BASE_URL}/api/temple-documents`, formData, authToken());
      message.success(`อัปโหลด "${file.name}" สำเร็จ`);
      await fetchDocuments();
    } catch (err) {
      message.error(err?.response?.data?.message || 'อัปโหลดไม่สำเร็จ');
    } finally {
      setUploading(false);
    }
    return false; // ป้องกัน antd upload อัตโนมัติ
  };

  const handleDelete = async (record) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/temple-documents/${record.id}`, authToken());
      message.success('ลบเอกสารเรียบร้อยแล้ว');
      fetchDocuments();
    } catch (err) {
      message.error('ไม่สามารถลบเอกสารได้');
    }
  };

  const columns = [
    {
      title: 'ไฟล์',
      dataIndex: 'original_name',
      key: 'original_name',
      render: (name, record) => (
        <Space>
          {fileIcon(name)}
          <a href={getFileUrl(record.ref, 'documents')} target="_blank" rel="noreferrer">
            {name}
          </a>
        </Space>
      ),
    },
    {
      title: 'ประเภท',
      dataIndex: 'original_name',
      key: 'type',
      width: 110,
      render: (name) => <Tag color="blue">{fileTypeLabel(name)}</Tag>,
    },
    {
      title: 'ขนาด',
      dataIndex: 'file_size',
      key: 'file_size',
      width: 110,
      render: (size) => formatBytes(size),
    },
    {
      title: 'อัปโหลดเมื่อ',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (d) => (d ? new Date(d).toLocaleString('th-TH') : '-'),
    },
    {
      title: 'การจัดการ',
      key: 'action',
      width: 230,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            type="link"
            icon={<EyeOutlined />}
            disabled={!isPreviewable(record.original_name)}
            onClick={() => setPreviewDoc(record)}
          >
            ดูเอกสาร
          </Button>
          <Button
            size="small"
            type="link"
            icon={<DownloadOutlined />}
            href={getFileUrl(record.ref, 'documents')}
            target="_blank"
          >
            ดาวน์โหลด
          </Button>
          <Popconfirm
            title="ลบเอกสารนี้?"
            description={`${record.original_name} จะถูกลบออกจากระบบ`}
            onConfirm={() => handleDelete(record)}
            okText="ลบ"
            cancelText="ยกเลิก"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              ลบ
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
    <Card title="เอกสารประวัติวัด" bordered={false}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="อัปโหลดเอกสารประวัติของวัด"
        description="รองรับไฟล์ PDF, รูปภาพ (jpg/jpeg/png/gif/webp/bmp), Word (doc/docx) และ Excel (xls/xlsx) ขนาดไม่เกิน 20MB ต่อไฟล์ — ไม่อนุญาตไฟล์วิดีโอ"
      />

      <Upload
        accept={ACCEPT_ATTR}
        beforeUpload={handleUpload}
        showUploadList={false}
        multiple
      >
        <Button type="primary" icon={<UploadOutlined />} loading={uploading} size="large">
          อัปโหลดเอกสาร
        </Button>
      </Upload>

      <div style={{ marginTop: 16 }}>
        <Table
          dataSource={documents}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
          locale={{
            emptyText: (
              <div style={{ padding: 24 }}>
                <FilePdfOutlined style={{ fontSize: 32, color: '#bfbfbf' }} />
                <div>ยังไม่มีเอกสารประวัติ — กดปุ่ม "อัปโหลดเอกสาร" เพื่อเพิ่มไฟล์ PDF/รูป/DOCX/XLSX</div>
              </div>
            ),
          }}
        />
      </div>
    </Card>

    {/* Modal พรีวิวเอกสารเต็มจอ (PDF / รูปภาพ) */}
    <Modal
      title={previewDoc?.original_name || 'ดูเอกสาร'}
      open={!!previewDoc}
      onCancel={() => setPreviewDoc(null)}
      footer={null}
      width="100%"
      style={{ top: 0, maxWidth: '100%', paddingBottom: 0 }}
      styles={{ body: { height: 'calc(100vh - 110px)', padding: 0, overflow: 'auto' } }}
    >
      {previewDoc && isPdf(previewDoc.original_name) && (
        <iframe
          src={getFileUrl(previewDoc.ref, 'documents')}
          title={previewDoc.original_name}
          style={{ border: 'none', width: '100%', height: '100%' }}
        />
      )}
      {previewDoc && isImage(previewDoc.original_name) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            height: '100%',
            background: '#f0f2f5',
          }}
        >
          <img
            src={getFileUrl(previewDoc.ref, 'documents')}
            alt={previewDoc.original_name}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        </div>
      )}
    </Modal>
    </>
  );
};

export default TempleDocuments;

